import { useState, type PropsWithChildren } from "react";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import type { Entity } from "types";
import { when } from "util/support";
import useGeneralActions from "features/general/useActions";
import { NotificationSeverity } from "features/general/types";
import type { DialogCloseHandler } from "ui/atoms/Modal";
import { TriggerableDialog } from "ui/molecules/Dialog";
import ActionPanel from "ui/molecules/ActionPanel";
import type { WithGridRowParams, OpenBankingResourcePivotActionProps } from "../types";

const OpenBankingResourcePivotAction = <T extends Entity>({
    id: rowId,
    pivotId,
    getOpenBankingResourcePivotActionProps,
    children,
    ...restProps
}: PropsWithChildren<WithGridRowParams<OpenBankingResourcePivotActionProps<T>>>) => {
    const {
        isLoading,
        onSuccess,
        ActionPanelProps,
        ModalProps,
        TriggerComponentProps: {
            icon,
            label,
            color,
            TooltipProps
        }
    } = getOpenBankingResourcePivotActionProps({
        ...restProps,
        id: rowId,
        pivotId
    });

    const [isProcessing, setProcessing] = useState(false);

    const { showNotication } = useGeneralActions();

    const handleSubmit = (onClose: DialogCloseHandler) => () => {
        setProcessing(true);

        return ActionPanelProps
            .onSave({ pki: pivotId ?? rowId })
            .then(({ success, message, data }) => {
                showNotication({
                    message,
                    severity: when(
                        success,
                        NotificationSeverity.Success,
                        NotificationSeverity.Error
                    )
                });

                when(success, () => {
                    onSuccess?.(data);
                    onClose();
                    ModalProps?.onClose?.();
                });
            })
            .finally(() => setProcessing(false));
    };

    return (
        <TriggerableDialog
            ModalProps={{
                disableCloseOnBackdropClick: true,
                ...ModalProps
            }}
            TriggerComponent={({ onClick }) => (
                <Tooltip
                    arrow
                    placement='top'
                    title={null}
                    {...TooltipProps}
                >
                    <GridActionsCellItem
                        disabled={isLoading || isProcessing}
                        onClick={onClick}
                        icon={icon ?? <></>}
                        label={label ?? ''}
                        color={color}
                        data-test-id={`openBanking.table.row.${rowId}.actions.${(label ?? '').toLowerCase()}`}
                    />
                </Tooltip>
            )}
            DialogActionsComponent={({ onClose }) => (
                <ActionPanel
                    {...ActionPanelProps}
                    sx={{
                        ...ActionPanelProps.sx,
                        flexGrow: 1
                    }}
                    isLoading={isProcessing}
                    onSave={handleSubmit(onClose)}
                    onCancel={() => {
                        onClose();
                        ModalProps?.onClose?.();
                    }}
                />
            )}
        >
            {ModalProps?.children ?? children}
        </TriggerableDialog>
    );
};

export default OpenBankingResourcePivotAction;
